/**
 * editableOriginal.ts — PLAT-10266 (epic PLAT-10209), Card 1 (tracer-bullet)
 *
 * Pure, surface-agnostic helpers for the review's EDITABLE ORIGINAL column:
 *   1. the index-space translation (`initial_index` / `source_uuid` → ARRAY
 *      POSITION) that the reused "Replace Term" chain consumes, and
 *   2. the in-session staleness selector that lights the overlapping translated
 *      fragment amber once its source text is edited.
 *
 * Both are pure functions of plain data — no store, no MobX, no DOM — so the
 * staleness read never mutates anything (mirrors `TranslationStore.checkCoverage`
 * reading live source). The wiring that captures the DOM selection and mounts the
 * reused components lives in `TranslationReviewWorkspace`; the amber CSS class is
 * emitted by `changedPhraseKit.translatedPhraseClassName` and painted by
 * `AlignedBlocksView`'s `TranslatedWord`.
 */

import { css } from 'styled-components';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * The amber tint + dashed underline for a STALE translated fragment (PLAT-10266
 * §6). A distinct hue from the four taken states: user-edited GREEN
 * (`--primary`), playhead / replace-lock GOLD (`--accent`), linked-selection
 * BLUE. `#f59e0b` matches the existing `--badge-orange` amber (theme.css), kept
 * off the gold `--accent` (`#f2d093`) so the two never read as the same thing.
 */
export const STALE_SOURCE_TINT = 'rgba(245, 158, 11, 0.22)';
export const STALE_SOURCE_UNDERLINE = 'rgba(245, 158, 11, 0.9)';

/**
 * Amber "stale" rule for a translated phrase `<Word>`. Interpolate into a
 * `styled(Word)` wrapper — `&` resolves to that wrapper. Applied whenever the
 * `stale` class is present; it deliberately yields the background to the PLAYING
 * playhead's gold fill (`.current:not(.cursor-before)`) exactly like the green
 * edited mark, keeping the dashed underline as the persistent stale cue.
 */
export const staleFragmentHighlightCss = css`
  &.stale:not(.current),
  &.stale.current.cursor-before {
    background-color: ${STALE_SOURCE_TINT};
    border-radius: 3px;
  }
  &.stale {
    text-decoration: underline dashed ${STALE_SOURCE_UNDERLINE};
    text-underline-offset: 3px;
  }
`;

/** The minimal element shape these helpers read. `state` is the transcript's own
 *  edit marker — set by the reversible edit commands to `'added'` / `'removed'` /
 *  `'cut'` / `'replaced'` — which the staleness selector reads (see §6). */
type SourceElement = Pick<
  TranscriptElement,
  'initial_index' | 'value' | 'id' | 'source_uuid' | 'state'
> & { subtype?: string; type?: string };

/**
 * Element `state` values that mark a source-text EDIT (the reused "Replace Term"
 * splices an `initial_index:-1` / `state:'added'` token; a delete / cut leaves a
 * struck `'removed'` / `'cut'` token; a directed replace stamps `'replaced'`).
 * `'muted'` is NOT here — muting is not a source-text edit.
 */
const SOURCE_EDIT_STATES: ReadonlySet<string> = new Set([
  'added',
  'removed',
  'cut',
  'replaced',
]);

/**
 * Stable join key for a source element — the SAME key the original column stamps
 * on `data-source-uuid` (`source_uuid ?? id`), falling back to the integer
 * `initial_index` when no id exists (`idx:<n>`). Drift-correct: it survives clip
 * id-regeneration and text edits (the edit keeps the id, only the value changes).
 */
export function sourceKeyOf(el: SourceElement): string {
  return el.source_uuid ?? el.id ?? `idx:${el.initial_index}`;
}

/** A content (word) element — a seek / edit / staleness target. Synthetic
 *  spacers (`initial_index < 0`) and whitespace are not. */
export function isContentElement(el: SourceElement): boolean {
  return el.initial_index >= 0 && el.subtype !== 'whitespace';
}

/**
 * A TEXT token as the backend counted them when it wrote `originalValueSnapshot`
 * (`arbor-v1 lib/translation/onDemandSplit.ts computeSnapshot`: `type === 'text'`,
 * punctuation excluded). Deliberately NOT `isContentElement` — the snapshot
 * reconstruction must still see a token whose `initial_index` is `-1` (an
 * in-session insert that inherited the replaced word's identity), and it must
 * still exclude punctuation, which `isContentElement` keeps. The value guard
 * covers fixtures/legacy rows that carry no `type` at all.
 */
function isSnapshotTextToken(el: SourceElement): boolean {
  if (el.type === 'punct') return false;
  if (el.subtype === 'whitespace') return false;
  return (el.value ?? '').trim() !== '';
}

/** A word picked out of the original column, addressed by both key spaces. */
export interface OriginalWordRef {
  /** `data-initial-index` — story-space integer, NOT the array position. */
  initialIndex: number;
  /** `data-source-uuid` — the drift-correct stable id, when present. */
  sourceUuid?: string | null;
  /**
   * `data-array-position` (Card 6) — the word's LIVE array position at render
   * time. Every rendered original word carries one, so this is the ONLY handle an
   * ADDED word has: a reused "Replace Term" edit drops the original token and
   * splices an `initial_index:-1` / `state:'added'` token that is excluded from
   * both lookup maps (`isContentElement`). Preferred when present + valid so an
   * added/replaced word is re-editable; the `source_uuid` / `initial_index`
   * resolution stays as the fallback for load-time words.
   */
  arrayPosition?: number | null;
}

export interface OriginalIndexMaps {
  /** stable id (`source_uuid ?? id`) → array position */
  byUuid: Map<string, number>;
  /** `initial_index` → array position */
  byInitialIndex: Map<number, number>;
}

/**
 * Build the two lookups that translate the original column's DOM stamps into
 * array positions in the flat `getFinalTranscriptionElements()` array. Content
 * elements only (mirrors `renderOriginalWords`' `posByInitialIndex`).
 */
export function buildOriginalIndexMaps(
  elements: readonly SourceElement[],
): OriginalIndexMaps {
  const byUuid = new Map<string, number>();
  const byInitialIndex = new Map<number, number>();
  elements.forEach((el, pos) => {
    if (!isContentElement(el)) return;
    if (el.initial_index >= 0 && !byInitialIndex.has(el.initial_index)) {
      byInitialIndex.set(el.initial_index, pos);
    }
    const uuid = el.source_uuid ?? el.id;
    if (uuid && !byUuid.has(uuid)) byUuid.set(uuid, pos);
  });
  return { byUuid, byInitialIndex };
}

/**
 * THE TOP GAP. Translate one selected original word to its ARRAY POSITION.
 * Prefer the stable id (`data-source-uuid`) — drift-correct, mirrors
 * `renderOriginalWords`' own resolution — then the `initial_index` map; return
 * `-1` when neither resolves (the caller drops the selection as a no-op). The
 * original column stamps `initial_index`, but the reused chain
 * (`WordReplacementModal`, `convertIndexToSegmentBoundaries` →
 * `transcriptElements[startIndex]`) consumes array positions, which drift from
 * `initial_index` the moment any word is inserted/edited.
 */
export function resolveArrayPosition(
  maps: OriginalIndexMaps,
  ref: OriginalWordRef,
): number {
  // Card 6 — a live array position is authoritative for ADDED words (which have
  // no source_uuid and `initial_index:-1`, so neither map resolves them). Prefer
  // it when present + valid; the id / index resolution below stays the fallback
  // for load-time words. (`resolveSelectionArrayRange` bounds-checks the result
  // against the live array so a stale/out-of-bounds stamp is dropped as a no-op.)
  if (
    ref.arrayPosition !== undefined &&
    ref.arrayPosition !== null &&
    Number.isInteger(ref.arrayPosition) &&
    ref.arrayPosition >= 0
  ) {
    return ref.arrayPosition;
  }
  if (ref.sourceUuid) {
    const byId = maps.byUuid.get(ref.sourceUuid);
    if (byId !== undefined) return byId;
  }
  const byIdx = maps.byInitialIndex.get(ref.initialIndex);
  return byIdx === undefined ? -1 : byIdx;
}

/**
 * Translate a captured original-column selection (two endpoints, either drag
 * direction) into the ascending array-position `{ startIndex, endIndex }` the
 * reused modal + `handleTranscriptTextReplace` expect. Returns `null` when
 * either endpoint is unresolved (drop the selection — never feed `-1` into the
 * replace chain, which would edit the wrong element or no-op silently).
 */
export function resolveSelectionArrayRange(
  elements: readonly SourceElement[],
  start: OriginalWordRef,
  end: OriginalWordRef,
): { startIndex: number; endIndex: number } | null {
  const maps = buildOriginalIndexMaps(elements);
  const a = resolveArrayPosition(maps, start);
  const b = resolveArrayPosition(maps, end);
  // Guard both endpoints: never feed -1 to the replace chain, and drop a
  // stale/out-of-bounds `data-array-position` stamp (Card 6) rather than index
  // past the live array — `convertIndexToSegmentBoundaries` reads
  // `transcriptElements[startIndex]`.
  if (a < 0 || b < 0 || a >= elements.length || b >= elements.length) {
    return null;
  }
  return { startIndex: Math.min(a, b), endIndex: Math.max(a, b) };
}

/** One load-time source word, keyed by `sourceKeyOf` in the baseline. */
export interface SourceBaselineEntry {
  /** The source word's text at load (retained for the stable return shape). */
  value: string;
  /**
   * The load-time ARRAY POSITION. The stale SIGNATURE (`computeStaleSignatures`)
   * uses it to anchor the covered-span window when a covered identity has VANISHED
   * (a single-word "Replace Term" drops the original token, so nothing in the
   * current array carries its id) — the load position still points at the slot the
   * replacement now occupies, so the signature can see (and re-key on) the new text.
   */
  pos: number;
}

/**
 * Capture the in-session source-text baseline at workspace load: `sourceKey →
 * { text, load-position }`, for every content element present. Pure snapshot — no
 * store, no mutation. Its KEYS are the load-time SET of source identities; the
 * staleness selector's vanished-identity check (§6, path b) treats a covered
 * identity that was in this set but is absent from the current array as the
 * remove+add signature (the reused edit DROPS the original token, so its identity
 * vanishes). The `value` is retained for the stable return shape; detection reads
 * the keys, and `computeStaleSignatures` reads `pos` for the vanish-anchor.
 */
export function captureSourceBaseline(
  elements: readonly SourceElement[],
): Map<string, SourceBaselineEntry> {
  const baseline = new Map<string, SourceBaselineEntry>();
  elements.forEach((el, pos) => {
    if (!isContentElement(el)) return;
    const key = sourceKeyOf(el);
    if (!baseline.has(key)) baseline.set(key, { value: el.value ?? '', pos });
  });
  return baseline;
}

/**
 * Reconstruct, BY IDENTITY, what a phrase's covered source words say NOW — the
 * string {@link computeStaleSignatures} compares against `originalValueSnapshot`
 * (its path c). Returns `null` when the span cannot be reconstructed, which is NOT
 * a verdict: an unresolvable span is neither "changed" nor "unchanged", so the
 * caller falls back to paths (a)/(b) rather than flagging everything or flagging
 * nothing.
 *
 * The rule:
 * - resolve each covered element by its stable key (`source_uuid ?? id`), the same
 *   key `sourceKeyOf` computes;
 * - keep TEXT tokens only and join them with a SINGLE space, matching how the
 *   backend wrote the snapshot (`arbor-v1 lib/translation/onDemandSplit.ts
 *   computeSnapshot`), so the comparison is like-for-like;
 * - require EVERY covered id to resolve to exactly one text token; anything else
 *   (a destroyed identity, or a `source_uuid` duplicated onto two words, which live
 *   data already contains) is unresolvable.
 *
 * ⚠️ NEVER keyed on `initial_index`. Inserting or deleting one word renumbers every
 * later index while every identity stays put, so an index-keyed reconstruction
 * reads a phrase's covered slots against its NEIGHBOUR's words and reports it as
 * changed when nothing about it changed. Identity does not shift, so keying on it
 * compares the phrase against itself. (The backend's own `computeSnapshot` is
 * index-keyed, but it runs once at translation time against the array it is
 * describing — it never re-reads an edited array, which is what this does.)
 *
 * `textPositionsByKey` is built once per `computeStaleSignatures` call because a
 * per-phrase scan of the element array would be O(phrases x elements) — thousands
 * of words times ~1.3k phrases on a real story.
 */
function reconstructCoveredSourceText(
  elements: readonly SourceElement[],
  textPositionsByKey: ReadonlyMap<string, number[]>,
  coveredIds: readonly string[],
): string | null {
  const positions: number[] = [];
  for (const id of new Set(coveredIds)) {
    const found = textPositionsByKey.get(id);
    if (found) positions.push(...found);
  }
  // One text token per covered id, no more and no less. A short count means an
  // identity is gone; a long count means an identity is not unique.
  if (positions.length !== coveredIds.length) return null;
  positions.sort((a, b) => a - b);
  return positions.map(pos => elements[pos]?.value ?? '').join(' ');
}

/**
 * The staleness selector (net-new, PURE — no store mutation). Refooted on the
 * transcript's own edit STATE + array-position coverage (§6, revised) after the
 * live tracer-bullet FALSIFIED the value-vs-baseline premise: the reused "Replace
 * Term" edit is a REMOVE-original-token + INSERT a new token stamped
 * `initial_index:-1` / `state:'added'` (TranscriptTextReplaceCommand +
 * transcriptTokenUtils), so the covered identity's `.value` never changes in
 * place and the inserted token is referenced by no phrase — the old compare saw
 * nothing (TRACE-4). Detect the edit directly instead.
 *
 * A translated fragment is STALE ⇔ EITHER
 *   (a) its covered ORIGINAL span — the min..max ARRAY POSITION of its covered
 *       elements, resolved `source_uuid` first then `initial_index`, the SAME
 *       slice `renderOriginalWords` computes (`AlignedBlocksView.tsx:717-735`) —
 *       in the CURRENT array contains an element whose `state` marks an edit
 *       (`SOURCE_EDIT_STATES`). For THIS scan the `initial_index:-1` inserted
 *       token is INCLUDED (the `isContentElement` `>= 0` exclusion is dropped
 *       here only) so an inline `state:'added'` insert is visible; OR
 *   (b) a source identity present in the LOAD-TIME baseline for that phrase is
 *       no longer present in the current array — the remove+add signature that
 *       drops the original token, which is the SINGLE-word case where (a) has no
 *       surviving covered element to anchor a span on; OR
 *   (c) PLAT-10266 Card I3 — the DURABLE snapshot comparison. The phrase's covered
 *       source text, reconstructed BY IDENTITY from the current array, differs from
 *       the `originalValueSnapshot` the backend captured when the phrase was
 *       translated. Paths (a) and (b) are both in-session signals: a save clears the
 *       edit-state markers and (once Cards I1/I2 make a replacement inherit the
 *       replaced word's identity) nothing vanishes from the array either, so an edit
 *       made in an EARLIER session leaves neither trace. Only the snapshot survives
 *       a reload. See {@link reconstructCoveredSourceText} for the rule and why it
 *       must never be keyed on `initial_index`.
 *
 * Retired (`active: false`) phrases are skipped — a retired fragment sharing a
 * source index is legal, not a signal. The `isContentElement` guard stays in
 * `buildOriginalIndexMaps` and the edit-selection path; only this detection sees
 * the `-1` token.
 *
 * `computeStaleSignatures` returns, per CURRENTLY-STALE fragment lead, a stable
 * SIGNATURE string capturing WHAT makes it stale — the sorted vanished covered
 * identities (path b), the `state:key:value` of the edit-state tokens in its
 * covered array-position window, and the reconstructed CURRENT covered text when
 * that reconstruction resolved (path c — without it a fragment resolved after a
 * snapshot-detected edit would stay silently suppressed when the SAME span is
 * edited again, because no other component of the signature would move). The
 * window is anchored by the covered elements'
 * CURRENT positions, falling back to their LOAD-TIME positions from the baseline
 * so a fully-vanished single-word fragment is still located. A NEW source edit to
 * the covered span yields a DIFFERENT signature; an unchanged span yields the SAME
 * one. This is the edit-keyed replacement for a permanent resolved-lead set
 * (PLAT-10266 F1): a resolved fragment is suppressed only while its signature is
 * unchanged since the resolve, so a fresh source edit re-flags it (DESIGN §6
 * "refresh on a fresh edit"). The DETECTION (which leads are stale) is unchanged —
 * `computeStaleLeads` is exactly the key set.
 */
export function computeStaleSignatures(
  baseline: ReadonlyMap<string, SourceBaselineEntry>,
  elements: readonly SourceElement[],
  phrases: readonly TranslationPhrase[],
): Map<number, string> {
  // Span-bound lookups (content elements only — the `-1` insert is never a
  // covered identity, it is what we DETECT inside the span). Mirrors
  // `renderOriginalWords`' `posBySourceUuid` / `posByInitialIndex`.
  const posByUuid = new Map<string, number>();
  const posByInitialIndex = new Map<number, number>();
  // The SET of source identities present in the CURRENT array (content elements),
  // for the vanished-identity check (path b).
  const currentIdentities = new Set<string>();
  // Card I3 — stable key → the ARRAY POSITIONS of the TEXT tokens carrying it, for
  // the durable snapshot reconstruction (path c). A separate index from the two
  // above because it deliberately includes tokens `isContentElement` rejects (an
  // insert stamped `initial_index: -1` that inherited the replaced word's identity)
  // and excludes punctuation, which `isContentElement` keeps. Every position is
  // kept — a duplicated identity has to stay visible so the span reads as
  // unresolvable instead of silently reconstructing the wrong text.
  const textPositionsByKey = new Map<string, number[]>();
  elements.forEach((el, pos) => {
    if (isSnapshotTextToken(el)) {
      const textKey = el.source_uuid ?? el.id;
      if (textKey != null) {
        const found = textPositionsByKey.get(textKey);
        if (found) found.push(pos);
        else textPositionsByKey.set(textKey, [pos]);
      }
    }
    if (!isContentElement(el)) return;
    if (el.initial_index >= 0 && !posByInitialIndex.has(el.initial_index)) {
      posByInitialIndex.set(el.initial_index, pos);
    }
    const uuid = el.source_uuid ?? el.id;
    if (uuid && !posByUuid.has(uuid)) posByUuid.set(uuid, pos);
    currentIdentities.add(sourceKeyOf(el));
  });

  // PLAT-10266 (2026-07-28) — does the array carry a spliced-IN token at all? A
  // covered id's absence is evidence that its token was spliced OUT only if
  // something was spliced in; without this guard a translation blob whose ids
  // simply do not belong to this transcript would turn EVERY fragment amber. A
  // pure delete/cut leaves a struck token whose id is still present, so the
  // vanished case always pairs with an insert.
  //
  // Tested STRUCTURALLY rather than on `state:'added'`: a later save can flatten
  // that marker (measured live — a subsequent edit re-saved the story and cleared
  // the earlier edit's `added` states), and staleness must not silently switch off
  // when it does. A CONTENT token carrying no story index (`initial_index < 0`)
  // came from no original transcript position, so it can only have been inserted.
  const arrayHasInsert = elements.some(
    el =>
      el.initial_index < 0 &&
      el.subtype !== 'whitespace' &&
      (el.value ?? '').trim() !== '',
  );

  const signatures = new Map<number, string>();
  for (const phrase of phrases) {
    if (phrase.active === false) continue;
    const indices = phrase.originalIndices;
    if (!indices || indices.length === 0) continue;
    const lead = Math.min(...indices);

    const ids = phrase.originalElementIds;
    const coveredKeys =
      ids && ids.length > 0 ? ids : indices.map(idx => `idx:${idx}`);

    // (b) A covered identity that was present at LOAD (a baseline key) has
    // vanished from the current array — the original token was dropped. Collect
    // ALL of them (the signature lists them; detection only needs one).
    const vanished: string[] = [];
    for (const key of coveredKeys) {
      if (currentIdentities.has(key)) continue;
      // Present at LOAD and now gone — the in-session remove+add signature.
      if (baseline.has(key)) {
        vanished.push(key);
        continue;
      }
      // PLAT-10266 (2026-07-28) — RELOAD DURABILITY. A covered STABLE ID missing
      // from the CURRENT array is proof the source token was spliced out, whether
      // or not THIS session's baseline ever saw it. A reload re-captures the
      // baseline from the already-edited array, so the check above sees nothing and
      // the amber flag — plus both resolve actions — disappeared for every edit made
      // in an earlier session (measured live: 0 stale fragments, "No changes yet",
      // logs/bug-popover-routing-20260728/diag-result.json). Restricted to the
      // stable-id space: an `idx:` key drifts the moment an edit renumbers the
      // array, so ITS absence proves nothing. Guarded on a real insert existing.
      if (ids && ids.length > 0 && arrayHasInsert) vanished.push(key);
    }

    // (c) PLAT-10266 Card I3 — the DURABLE comparison. Reconstruct the covered
    // source text by IDENTITY and hold it against the snapshot the backend captured
    // when the phrase was translated. Layered so nothing regresses:
    //   - no snapshot (measured: 178 of 1259 live phrases, all active, all with ids
    //     and translated text) ⇒ `currentText` stays null and (a)/(b) decide, which
    //     is exactly today's behaviour;
    //   - no `originalElementIds` ⇒ also null. The index space is NOT an acceptable
    //     substitute here (see `reconstructCoveredSourceText`);
    //   - covered identities unresolvable (the already-damaged fragments, whose ids
    //     were destroyed before Cards I1/I2 existed) ⇒ null, so an empty
    //     reconstruction never masquerades as "differs" or as "matches".
    // Path (c) only ever ADDS staleness: it is OR-ed with (a)/(b) rather than
    // replacing them, because a covered word that was DELETED keeps both its
    // identity and its text (it is merely stamped `state:'removed'`), so the
    // reconstruction reports "unchanged" for an edit that path (a) genuinely sees.
    // Letting (c) overrule (a)/(b) would switch that marker off — the same silent
    // loss this card exists to prevent.
    const snapshot = phrase.originalValueSnapshot;
    const currentText =
      snapshot && ids && ids.length > 0
        ? reconstructCoveredSourceText(elements, textPositionsByKey, ids)
        : null;
    const snapshotDiffers = currentText !== null && currentText !== snapshot;

    let isStale = vanished.length > 0 || snapshotDiffers;

    // (a) The covered ARRAY-POSITION span contains an edit-state token. Resolve
    // the span bounds `source_uuid` first (drift-correct), then `initial_index`.
    // (unchanged detection — surviving-identity positions only.)
    if (!isStale) {
      let firstPos = Infinity;
      let lastPos = -Infinity;
      if (ids && ids.length > 0) {
        for (const id of ids) {
          const pos = posByUuid.get(id);
          if (pos === undefined) continue;
          if (pos < firstPos) firstPos = pos;
          if (pos > lastPos) lastPos = pos;
        }
      }
      if (firstPos === Infinity) {
        for (const idx of indices) {
          const pos = posByInitialIndex.get(idx);
          if (pos === undefined) continue;
          if (pos < firstPos) firstPos = pos;
          if (pos > lastPos) lastPos = pos;
        }
      }
      if (firstPos !== Infinity) {
        for (let pos = firstPos; pos <= lastPos; pos++) {
          const el = elements[pos];
          if (el && el.state && SOURCE_EDIT_STATES.has(el.state)) {
            isStale = true;
            break;
          }
        }
      }
    }

    if (!isStale) continue;

    // SIGNATURE window — anchor on the covered elements' CURRENT positions AND,
    // for any covered identity that VANISHED, its LOAD position (so the content
    // that replaced a fully-vanished single-word fragment is captured, and
    // changes on a re-edit).
    let lo = Infinity;
    let hi = -Infinity;
    const consider = (p: number) => {
      if (p < lo) lo = p;
      if (p > hi) hi = p;
    };
    if (ids && ids.length > 0) {
      for (const id of ids) {
        const cp = posByUuid.get(id);
        if (cp !== undefined) consider(cp);
        else {
          const b = baseline.get(id);
          if (b) consider(b.pos);
        }
      }
    } else {
      for (const idx of indices) {
        const cp = posByInitialIndex.get(idx);
        if (cp !== undefined) consider(cp);
        else {
          const b = baseline.get(`idx:${idx}`);
          if (b) consider(b.pos);
        }
      }
    }
    // Card 6 — extend the window FORWARD across the contiguous run of inserted
    // edit-state tokens (+ the whitespace spacers between them). A single-word /
    // whole-span "Replace Term" DROPS the covered token(s) and splices a RUN of
    // `state:'added'` tokens at the anchor; without this the window pins to the
    // covered element's single load position and a re-edit of any replacement
    // token PAST THE FIRST leaves the signature unchanged — so a resolved
    // fragment would not re-flag amber (Denys: replacing "X" with "a b c" then
    // re-editing "b" must go stale). Bounded at the first surviving content word.
    if (lo !== Infinity) {
      let scan = hi + 1;
      while (scan < elements.length) {
        const el = elements[scan];
        if (!el) break;
        const isWs = typeof el.value === 'string' && el.value.trim() === '';
        const isEdit = !!el.state && SOURCE_EDIT_STATES.has(el.state);
        if (isEdit) {
          hi = scan;
          scan++;
          continue;
        }
        if (isWs) {
          scan++;
          continue;
        }
        break;
      }
    }
    const editTokens: string[] = [];
    if (lo !== Infinity) {
      const from = Math.max(0, lo);
      const to = Math.min(elements.length - 1, hi);
      for (let pos = from; pos <= to; pos++) {
        const el = elements[pos];
        if (el && el.state && SOURCE_EDIT_STATES.has(el.state)) {
          editTokens.push(`${el.state}:${sourceKeyOf(el)}:${el.value ?? ''}`);
        }
      }
    }
    vanished.sort();
    // The reconstructed CURRENT text joins the signature whenever it resolved, so a
    // fragment resolved after a snapshot-detected edit RE-FLAGS when the same span
    // is edited again. Nothing else in the signature moves in that case: after a
    // save there is no edit-state token to list and no identity has vanished.
    const sourceText = currentText === null ? '' : `s[${currentText}]`;
    signatures.set(
      lead,
      `v[${vanished.join(',')}]e[${editTokens.join(';')}]${sourceText}`,
    );
  }
  return signatures;
}

/**
 * The set of stale fragment LEADS (`min(originalIndices)`, the key
 * `data-initial-index` + the coverage cache use) — the KEYS of
 * `computeStaleSignatures`. A thin wrapper so callers that only need the lead set
 * (and the existing tests) are unaffected by the edit-keyed signature refactor.
 */
export function computeStaleLeads(
  baseline: ReadonlyMap<string, SourceBaselineEntry>,
  elements: readonly SourceElement[],
  phrases: readonly TranslationPhrase[],
): Set<number> {
  return new Set(computeStaleSignatures(baseline, elements, phrases).keys());
}

/**
 * The vanished-source fallback for a re-translate (PLAT-10266 Card 7, §D). The
 * result of resolving a covered span from the CURRENT array when
 * `collectSpanSourceElements` returned `null`: a "Replace Term" edit DELETED the
 * covered token (spliced it out) and inserted `state:'added'` replacement tokens
 * at the same slot, so the phrase's dead ids no longer place a span. Reuse the
 * load-time baseline `pos` (the vanish-anchor `SourceBaselineEntry.pos`) + the
 * SAME contiguous added-run walk `computeStaleSignatures` uses to slice the added
 * tokens as the re-translate SOURCE, and return their fresh element ids so the
 * caller can re-key the authored override to the live tokens (else it re-orphans).
 */
export interface VanishedSpanSource {
  /**
   * The contiguous source slice at the baseline slot — the added-token run (plus
   * the whitespace interleaved between the added tokens), in array order. Fed to
   * the backend re-translate as the span's source (so it translates "wolf pack",
   * not the vanished original).
   */
  sourceElements: TranscriptElement[];
  /**
   * The added run's live CONTENT tokens' fresh element ids (`source_uuid ?? id`),
   * source order — the durable keys to re-anchor the authored override on so it
   * renders by identity (render membership matches by `source_uuid ?? id`).
   */
  freshElementIds: string[];
}

/**
 * Resolve a VANISHED covered span (see {@link VanishedSpanSource}). Returns
 * `null` when nothing vanished (the covered ids are still present → the normal
 * `collectSpanSourceElements` path applies) or when the slot holds no live
 * replacement token (a pure delete with nothing to re-translate). Pure — reads
 * the baseline + current array only.
 */
export function resolveVanishedSpanSource(
  baseline: ReadonlyMap<string, SourceBaselineEntry>,
  elements: readonly TranscriptElement[],
  coveredElementIds: readonly string[],
  coveredIndices: readonly number[],
): VanishedSpanSource | null {
  // Identities present in the CURRENT array (content elements only — the same
  // key space the baseline uses). A covered identity in the baseline but absent
  // here has VANISHED (its token was spliced out).
  const currentKeys = new Set<string>();
  for (const el of elements) {
    if (!isContentElement(el)) continue;
    currentKeys.add(sourceKeyOf(el));
  }
  const coveredKeys =
    coveredElementIds.length > 0
      ? coveredElementIds
      : coveredIndices.map(idx => `idx:${idx}`);

  // Anchor on the EARLIEST load position among the vanished covered identities —
  // a single-word "Replace Term" splices its added run exactly where the dropped
  // token sat, so this load `pos` still points at the slot the replacement now
  // occupies (the retained `SourceBaselineEntry.pos` contract).
  let anchor = Infinity;
  for (const key of coveredKeys) {
    const entry = baseline.get(key);
    if (entry && !currentKeys.has(key) && entry.pos < anchor) {
      anchor = entry.pos;
    }
  }
  if (anchor === Infinity) return null; // nothing vanished → not a fallback case

  // Walk FORWARD from the slot across the contiguous run of edit-state tokens
  // (+ the whitespace spacers between them), bounded at the first surviving
  // content word — the SAME walk `computeStaleSignatures` uses for its signature
  // window (editableOriginal.ts, "extend the window FORWARD").
  let runEnd = anchor - 1;
  let scan = anchor;
  while (scan < elements.length) {
    const el = elements[scan];
    if (!el) break;
    const isWs = typeof el.value === 'string' && el.value.trim() === '';
    const isEdit = !!el.state && SOURCE_EDIT_STATES.has(el.state);
    if (isEdit) {
      runEnd = scan;
      scan++;
      continue;
    }
    if (isWs) {
      scan++;
      continue;
    }
    break;
  }
  if (runEnd < anchor) return null; // no replacement token at the slot

  const sourceElements = (elements as TranscriptElement[]).slice(
    anchor,
    runEnd + 1,
  );
  const freshElementIds: string[] = [];
  for (const el of sourceElements) {
    // Struck tokens (a redline cut/removed inside the run) are not live text —
    // never key an override on them.
    if (el.state === 'removed' || el.state === 'cut') continue;
    const isWs = typeof el.value === 'string' && el.value.trim() === '';
    if (isWs) continue;
    const id = el.source_uuid ?? el.id;
    if (id != null) freshElementIds.push(id);
  }
  if (freshElementIds.length === 0) return null; // no live token to re-key on

  return { sourceElements, freshElementIds };
}
